'use client';

import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { Mail, Phone, MessageCircle } from 'lucide-react';
import styles from './ContactCTA.module.css';

const contactMethods = [
    {
        icon: <Mail size={28} />,
        label: "Email Us",
        value: "info@rotanabiz.com",
        href: "mailto:info@rotanabiz.com",
        delay: 0
    },
    {
        icon: <MessageCircle size={28} />,
        label: "WhatsApp",
        value: "+971 58 8181824",
        href: "https://wa.me/971588181824",
        delay: 0.1
    },
    {
        icon: <Phone size={28} />,
        label: "Call Support",
        value: "+971 7 2430438",
        href: "tel:+97172430438",
        delay: 0.2
    }
];

interface ContactCTAProps {
    title?: string;
    highlight?: string;
    description?: string;
}

export default function ContactCTA({
    title = "Start Your Business",
    highlight = "Without the Headache.",
    description = "Speak directly to our setup experts. No call centers, just real advisors ready to help you launch."
}: ContactCTAProps) {
    return (
        <section className={styles.section}>
            <div className={styles.bgGlow} />

            <div className={`container ${styles.contentWrapper}`}>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className={styles.title}>
                        {title} <br />
                        <span className={styles.highlight}>{highlight}</span>
                    </h2>
                    <p className={styles.subtitle}>
                        {description}
                    </p>
                </motion.div>

                <div className={styles.grid}>
                    {contactMethods.map((method, index) => (
                        <ContactCard key={index} method={method} index={index} />
                    ))}
                </div>

                <motion.div
                    className={styles.footerNote}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    <span className={styles.dot} />
                    <span>Our advisors are currently <strong>online</strong> and responding in ~5 mins.</span>
                </motion.div>

            </div>
        </section>
    );
}

function ContactCard({ method, index }: any) {
    const cardRef = useRef<HTMLAnchorElement>(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <motion.a
            ref={cardRef}
            key={index}
            href={method.href}
            className={`${styles.card} glass-card-hover`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: method.delay, duration: 0.5 }}
            onMouseMove={handleMouseMove}
        >
            <div
                className={styles.cardGlow}
                style={{ left: mousePos.x, top: mousePos.y }}
            />
            <div className={styles.iconWrapper}>
                {method.icon}
            </div>
            <span className={styles.label}>{method.label}</span>
            <span className={styles.value}>{method.value}</span>
        </motion.a>
    );
}
